import React, { useCallback, useState } from 'react';
import { InlineMath } from 'react-katex';
import { createApiUrl } from '../../../config/api';
import { useApiAuth } from '../../../hooks/useApiAuth';
import type { OptimizationProblem, VariableDef } from '../../../types/optimization';
import type { ChatMessage } from '../../ChatBox/Messages';
import RangeSlider from './BoundsSlider';
import CollapsiblePanel from './CollapsiblePanel';
import './optimization.css';

interface OptimizationProps {
  optimizationData: OptimizationProblem;
  userID?: string;
  sessionId?: string;
  onOptimizationMessage?: (message: ChatMessage) => void;
  onResponse?: (data: any[]) => void;
}

const Optimization: React.FC<OptimizationProps> = ({ 
  optimizationData, 
  userID, 
  sessionId,
  onOptimizationMessage,
  onResponse
}) => {
  const { makeAuthenticatedRequest } = useApiAuth();
  const [isOptimizing, setIsOptimizing] = useState(false);
  const [editableVariables, setEditableVariables] = useState<VariableDef[]>(optimizationData?.variables || []);
  const [collapsedPanels, setCollapsedPanels] = useState<{[key: string]: boolean}>({
    objective: false,
    variables: false,
    constraints: false
  });
  const [showExpressionModal, setShowExpressionModal] = useState(false);

  // Update editable variables when optimizationData changes
  React.useEffect(() => {
    if (optimizationData?.variables) {
      setEditableVariables(optimizationData.variables);
    }
  }, [optimizationData]);

  // Variables change handler for child component
  const handleVariablesChange = (vars: VariableDef[]) => setEditableVariables(vars);

  // Toggle panel collapse
  const togglePanel = (panelKey: string) => {
    setCollapsedPanels(prev => ({
      ...prev,
      [panelKey]: !prev[panelKey]
    }));
  };

  // Handle optimization
  const handleOptimize = useCallback(async () => {
    if (!sessionId) {
      console.error('Missing sessionId for optimization');
      return;
    }

    setIsOptimizing(true);

    // Send initial optimization message immediately when user clicks optimize
    const initialMessageId = `optimization-${Date.now()}`;
    const messageCreatedAt = new Date(); // Capture timestamp once for consistent ordering
    if (onOptimizationMessage) {
      onOptimizationMessage({
        role: 'assistant',
        content: 'Starting optimization...',
        id: initialMessageId,
        createdAt: messageCreatedAt
      });
    }
    
    try {
      console.log('Starting optimization with updated variables:', editableVariables);
      
      // Create the payload using the backend schema directly
      const optimizationPayload: OptimizationProblem = {
        problem_name: optimizationData.problem_name,
        variables: editableVariables,
        constraints: optimizationData.constraints,
        objective: optimizationData.objective
      };
      
      console.log('Sending optimization payload:', optimizationPayload);
      
      const response = await makeAuthenticatedRequest(createApiUrl(`/optimization/optimize_model/session_id/${sessionId}`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(optimizationPayload)
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('API Error:', errorText);
        throw new Error(`HTTP error! status: ${response.status}, body: ${errorText}`);
      }

      // Handle streaming response
      const reader = response.body?.getReader();
      if (!reader) {
        throw new Error('No response body');
      }
      
      const decoder = new TextDecoder();
      let accumulatedText = '';
      let messageId = initialMessageId; // Use the same ID to update the initial message
      
      try {
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          
          const chunk = decoder.decode(value, { stream: true });
          
          const lines = chunk.split('\n');

          for (const line of lines) {
            if (line.trim()) {
              // Handle AI SDK streaming protocol text tokens (format like 0:"text here")
              if (line.includes('"') && line.includes(':')) {
                try {
                  // Parse token to extract text from format like 0:"text here"
                  const colonIndex = line.indexOf(':');
                  if (colonIndex >= 0) {
                    const afterColon = line.slice(colonIndex + 1);
                    if (afterColon.startsWith('"') && afterColon.endsWith('"')) {
                      // Use JSON.parse to properly handle all escape sequences (\\, \n, \u00b2, etc.)
                      const text = JSON.parse(afterColon);
                      accumulatedText += text;
                      
                      // Send text updates to chat
                      if (onOptimizationMessage) {
                        onOptimizationMessage({
                          role: 'assistant',
                          content: accumulatedText,
                          id: messageId,
                          createdAt: messageCreatedAt
                        });
                      }
                    }
                  }
                } catch (e) {
                  console.error('Error parsing text token:', e);
                }
              }
            }
          }
        }
        
        // Send final completion message to replace any remaining optimization text
        if (onOptimizationMessage) {
          onOptimizationMessage({
            role: 'assistant',
            content: accumulatedText || 'Optimization completed successfully.',
            id: messageId,
            createdAt: messageCreatedAt
          });
        }
      } finally {
        reader.releaseLock();
      }
    } catch (error) {
      console.error('Optimization failed:', error);
    } finally {
      setIsOptimizing(false);
    }
  }, [sessionId, editableVariables, onResponse, optimizationData, onOptimizationMessage, makeAuthenticatedRequest]);

  if (!optimizationData) {
    return (
      <div className="w-full h-full flex items-center justify-center text-gray-500 bg-gray-50">
        <div className="text-center">
          <div className="text-lg font-medium">No Optimization Problem</div>
          <div className="text-sm">Ask the AI to create an optimization problem</div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full h-full flex flex-col overflow-hidden bg-gray-50">
      {/* Expression Modal */}
      {showExpressionModal && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onClick={() => setShowExpressionModal(false)}
        >
          <div 
            className="bg-white rounded-lg shadow-2xl max-w-2xl w-full max-h-[80vh] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="bg-gradient-to-r from-blue-500 to-blue-600 px-6 py-4 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-white">Objective Function Expression</h3>
              <button
                onClick={() => setShowExpressionModal(false)}
                className="text-white hover:text-gray-200 transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="p-6 overflow-y-auto max-h-[calc(80vh-80px)]">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-2">Optimization Type</label>
                  <div className="px-4 py-2 bg-blue-50 text-blue-700 rounded-md inline-block font-medium uppercase text-sm">
                    {optimizationData.objective.sense}
                  </div>
                </div>
                {optimizationData.objective.name && (
                  <div>
                    <label className="block text-sm font-medium text-gray-600 mb-2">Function Name</label>
                    <div className="px-4 py-3 bg-gray-50 rounded-md font-mono text-sm text-gray-800">
                      {optimizationData.objective.name}
                    </div>
                  </div>
                )}
                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-2">Full Expression</label>
                  <div className="px-4 py-3 bg-gray-50 rounded-md font-mono text-sm text-gray-800 whitespace-pre-wrap break-words">
                    {optimizationData.objective.expr}
                  </div>
                </div>
              </div>
            </div>
            <div className="bg-gray-50 px-6 py-4 flex justify-end border-t border-gray-200">
              <button
                onClick={() => setShowExpressionModal(false)}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors font-medium"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex-shrink-0 bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center">
              <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
            </div>
            <div>
              <h1 className="text-lg font-semibold text-gray-800">Optimization Problem</h1>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={handleOptimize}
              disabled={isOptimizing || !userID || !sessionId}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-lg font-medium text-sm transition-all ${
                isOptimizing || !userID || !sessionId
                  ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                  : 'bg-blue-600 text-white hover:bg-blue-700 shadow-sm hover:shadow-md'
              }`}
            >
              {isOptimizing ? (
                <>
                  <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                  Optimizing...
                </>
              ) : (
                'Optimize'
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Optimization Problem Content */}
      <div className="flex-1 overflow-y-auto overflow-x-hidden min-h-0 p-6">
        <div className="max-w-4xl mx-auto space-y-6 pb-6">
          <CollapsiblePanel
            title="Problem Overview"
            isCollapsed={collapsedPanels.objective}
            onToggle={() => togglePanel('objective')}
          >
            <div className="space-y-3">
              <div>
                <span className="font-medium text-gray-600">Problem Name:</span>
                <span className="ml-2 text-gray-800">{optimizationData.problem_name}</span>
              </div>
              <div>
                <span className="font-medium text-gray-600">Objective Function:</span>
                <div className="mt-1 p-3 bg-gray-50 rounded-md relative group">
                  <div className="mb-1 flex items-center justify-between">
                    <span className="text-xs text-gray-500 uppercase">{optimizationData.objective.sense}</span>
                    <button
                      onClick={() => setShowExpressionModal(true)}
                      className="text-xs text-blue-600 hover:text-blue-700 hover:underline flex items-center gap-1 transition-colors"
                      title="View full expression"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      View Expression
                    </button>
                  </div>
                  <div className="font-mono text-sm text-gray-800">
                    {optimizationData.objective.name || optimizationData.objective.expr}
                  </div>
                </div>
              </div>
            </div>
          </CollapsiblePanel>

          <CollapsiblePanel
            title="Variables"
            isCollapsed={collapsedPanels.variables}
            onToggle={() => togglePanel('variables')}
          >
            <div className="space-y-6">
              {editableVariables.map((variable, index) => {
                return (
                  <div key={index} className="border border-gray-200 rounded-lg p-6 bg-gray-50 flex-shrink-0">
                    <div className="space-y-6">
                      <div className="flex items-center gap-4">
                        <div className="flex-shrink-0">
                          <label className="block text-sm font-medium text-gray-700 mb-2">Symbol</label>
                          <div className="bg-white border border-gray-300 rounded-md px-3 py-2 h-10 flex items-center variable-symbol-container">
                            {variable.standard_symbol ? (
                              <div className="optimization-katex-container">
                                <InlineMath math={variable.standard_symbol} />
                              </div>
                            ) : (
                              <span className="text-gray-500 italic">No symbol</span>
                            )}
                          </div>
                        </div>
                        <div className="flex-grow">
                          <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                          <div className="bg-white border border-gray-300 rounded-md px-3 py-2 h-10 flex items-center">
                            <span className="text-gray-700 truncate">{variable.description || 'No description'}</span>
                          </div>
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Bounds</label>
                        <div className="space-y-3">
                          {variable.bounds && variable.bounds.lower !== null && variable.bounds.upper !== null && (
                            <RangeSlider
                              initialBounds={{
                                lower: variable.bounds.lower ?? 0,
                                upper: variable.bounds.upper ?? 100
                              }}
                              value={{
                                lower: variable.bounds.lower ?? 0,
                                upper: variable.bounds.upper ?? 100
                              }}
                              onChange={(b) => {
                                const next = editableVariables.map((v, i) => (i === index ? { 
                                  ...v, 
                                  bounds: { lower: b.lower, upper: b.upper }
                                } : v));
                                handleVariablesChange(next);
                              }}
                              initialGuess={variable.initial_value}
                              onInitialGuessChange={(val) => {
                                const next = editableVariables.map((v, i) => (i === index ? { ...v, initial_value: val } : v));
                                handleVariablesChange(next);
                              }}
                              unitsLatex={variable.units}
                            />
                          )}
                          {(variable.bounds.lower === null || variable.bounds.upper === null) && (
                            <div className="text-sm text-gray-500 italic">
                              Unbounded variable (lower: {variable.bounds.lower ?? "∞"}, upper: {variable.bounds.upper ?? "∞"})
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </CollapsiblePanel>

          <CollapsiblePanel
            title="Constraints"
            isCollapsed={collapsedPanels.constraints}
            onToggle={() => togglePanel('constraints')}
          >
            <div className="space-y-2">
              {optimizationData.constraints && optimizationData.constraints.length > 0 ? (
                optimizationData.constraints?.map((constraint, index) => (
                  <div key={index} className="p-3 bg-gray-50 rounded-md font-mono text-sm">
                    <div className="mb-1">
                      <span className="text-xs text-gray-500 uppercase">{constraint.type}</span>
                    </div>
                    {constraint.expr} {constraint.sense} 0
                  </div>
                ))
              ) : (
                <p className="text-gray-500 italic">No constraints defined</p>
              )}
            </div>
          </CollapsiblePanel>
        </div>
      </div>
    </div>
  );
};

export default Optimization;
